import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError, formatTanggal } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_ADMIN, NAV_KEPSEK } from "@/lib/nav";
import { FilterBar } from "@/components/FilterBar";
import { toast } from "sonner";
import { Lock, LockOpen, MessageSquare, X } from "lucide-react";

const F_EMPTY = { q: "", status: "" };
const STATUS_OPTS = [
  { v: "tertutup", label: "Belum Dibuka" },
  { v: "catatan", label: "Ada Catatan (Belum Dibuka)" },
  { v: "dibuka", label: "Dibuka — Belum Konfirmasi" },
  { v: "sudah_konfirmasi", label: "Sudah Konfirmasi" },
];
const STATUS_BADGE = {
  tertutup: { label: "Belum Dibuka", cls: "pp-badge-secondary" },
  catatan: { label: "Ada Catatan", cls: "pp-badge-warning" },
  dibuka: { label: "Dibuka — Belum Konfirmasi", cls: "pp-badge-info" },
  sudah_konfirmasi: { label: "Sudah Konfirmasi", cls: "pp-badge-success" },
};
const TA_OPTS = ["2025/2026", "2026/2027", "2027/2028", "2028/2029"];

function StatusBadge({ s }) {
  const b = STATUS_BADGE[s] || STATUS_BADGE.tertutup;
  return <span className={b.cls}>{b.label}</span>;
}

export function DaftarUlangPage({ nav, canWrite }) {
  const [payload, setPayload] = useState(null);
  const [filters, setFilters] = useState(F_EMPTY);
  const [applied, setApplied] = useState(F_EMPTY);
  const [ta, setTa] = useState("");
  const [catatanItem, setCatatanItem] = useState(null);
  const [catatanText, setCatatanText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/daftar-ulang"); setPayload(data); setTa(data.tahun_ajaran); }
    catch (e) { console.error(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const items = (payload?.items || []).filter((i) => {
    if (applied.q && !i.nama.toLowerCase().includes(applied.q.toLowerCase())) return false;
    if (applied.status && i.status !== applied.status) return false;
    return true;
  });
  const stats = payload?.stats || {};

  const bulk = async (action, all, siswaIds) => {
    const label = action === "buka" ? "membuka" : "menutup";
    const target = all ? "SEMUA siswa aktif" : `${siswaIds.length} siswa`;
    if (!window.confirm(`Yakin ${label} menu daftar ulang untuk ${target}?`)) return;
    try {
      const { data } = await api.post(`/daftar-ulang/${action}`, { all, siswa_ids: siswaIds });
      toast.success(data.message);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveTa = async () => {
    try { await api.put("/daftar-ulang/pengaturan/tahun-ajaran", { tahun_ajaran: ta }); toast.success(`Tahun ajaran daftar ulang: ${ta}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveCatatan = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/daftar-ulang/${catatanItem.siswa_id}/catatan`, { catatan: catatanText });
      toast.success("Catatan disimpan");
      setCatatanItem(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Layout nav={nav}>
      <PageTitle title="Daftar Ulang" subtitle={`Tahun Ajaran Baru ${payload?.tahun_ajaran || "—"}`} />

      {canWrite && (
        <section className="pp-card p-5 mb-4 flex flex-wrap items-end gap-3" data-testid="du-controls">
          <div className="min-w-[180px]">
            <label className="pp-label">Tahun Ajaran Baru</label>
            <select className="pp-input" value={ta} onChange={(e) => setTa(e.target.value)} data-testid="du-tahun-ajaran">
              {TA_OPTS.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <button onClick={saveTa} className="pp-btn-secondary" data-testid="du-save-ta">Simpan TA</button>
          <div className="flex-1" />
          <button onClick={() => bulk("buka", true, [])} className="pp-btn-primary" data-testid="du-buka-semua">
            <LockOpen className="w-4 h-4" /> Buka Semua
          </button>
          <button onClick={() => bulk("tutup", true, [])} className="pp-btn-secondary text-rose-600" data-testid="du-tutup-semua">
            <Lock className="w-4 h-4" /> Tutup Semua
          </button>
        </section>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4" data-testid="du-stats">
        {[
          { label: "Total Siswa Aktif", value: stats.total ?? 0, color: "text-slate-800" },
          { label: "Belum Dibuka", value: (stats.tertutup ?? 0) + (stats.catatan ?? 0), color: "text-slate-500" },
          { label: "Punya Catatan", value: stats.catatan ?? 0, color: "text-amber-600" },
          { label: "Dibuka — Belum Konfirmasi", value: stats.dibuka ?? 0, color: "text-indigo-600" },
          { label: "Sudah Konfirmasi", value: stats.sudah_konfirmasi ?? 0, color: "text-emerald-600" },
        ].map((c) => (
          <div key={c.label} className="pp-card p-4 text-center">
            <div className={`text-2xl font-bold ${c.color}`}>{c.value}</div>
            <div className="text-xs text-slate-500 mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      <FilterBar
        filters={filters} setFilters={setFilters}
        onApply={() => setApplied({ ...filters })}
        onReset={() => { setFilters(F_EMPTY); setApplied(F_EMPTY); }}
        dataTestidPrefix="du-filter"
        fields={[
          { key: "q", type: "text", label: "Nama Siswa", placeholder: "Cari nama siswa" },
          { key: "status", type: "select", label: "Status", options: STATUS_OPTS },
        ]}
      />

      <section className="pp-card pp-freeze overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1000px] w-full text-sm" data-testid="du-table">
            <thead><tr className="pp-table-header">
              <th className="text-left px-5 py-3">Nama Siswa</th>
              <th className="text-left px-5 py-3">NISN</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-left px-5 py-3">Catatan Admin</th>
              <th className="text-left px-5 py-3">Tgl Konfirmasi</th>
              {canWrite && <th className="text-right px-5 py-3">Aksi</th>}
            </tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={canWrite ? 6 : 5} className="text-center py-10 text-slate-400 italic">Tidak ada data.</td></tr>}
              {items.map((i) => (
                <tr key={i.siswa_id} className="pp-row" data-testid={`du-row-${i.siswa_id}`}>
                  <td className="px-5 py-3 font-medium">{i.nama}</td>
                  <td className="px-5 py-3 font-mono text-xs">{i.nis || "—"}</td>
                  <td className="px-5 py-3"><StatusBadge s={i.status} /></td>
                  <td className="px-5 py-3 text-xs text-slate-600 max-w-[260px]">
                    {i.catatan ? <span className="whitespace-pre-wrap">{i.catatan}</span> : "—"}
                  </td>
                  <td className="px-5 py-3 text-xs">{i.confirmed_at ? formatTanggal(i.confirmed_at) : "—"}</td>
                  {canWrite && (
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      {i.dibuka ? (
                        <button onClick={() => bulk("tutup", false, [i.siswa_id])} className="pp-btn-secondary text-xs text-rose-600 mr-1" data-testid={`du-tutup-${i.siswa_id}`}>
                          <Lock className="w-3.5 h-3.5" /> Tutup
                        </button>
                      ) : (
                        <button onClick={() => bulk("buka", false, [i.siswa_id])} className="pp-btn-primary text-xs mr-1" data-testid={`du-buka-${i.siswa_id}`}>
                          <LockOpen className="w-3.5 h-3.5" /> Buka
                        </button>
                      )}
                      <button onClick={() => { setCatatanItem(i); setCatatanText(i.catatan || ""); }} className="pp-btn-ghost text-xs" data-testid={`du-catatan-${i.siswa_id}`}>
                        <MessageSquare className="w-3.5 h-3.5" /> Catatan
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {catatanItem && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="du-catatan-modal">
          <div className="pp-card p-6 w-full max-w-lg animate-fade-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Catatan untuk Ortu — {catatanItem.nama}</h3>
              <button onClick={() => setCatatanItem(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Jika catatan diisi, menu Daftar Ulang di login ortu akan <b>muncul</b> namun ortu <b>belum bisa konfirmasi</b> — catatan ini yang akan mereka lihat (contoh: kewajiban melunasi SPP).
            </p>
            <form onSubmit={saveCatatan} className="space-y-3">
              <textarea className="pp-input" rows={4} value={catatanText} onChange={(e) => setCatatanText(e.target.value)}
                placeholder="Contoh: Mohon melunasi tunggakan SPP terlebih dahulu sebelum melakukan daftar ulang."
                data-testid="du-catatan-text" />
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setCatatanItem(null)} className="pp-btn-secondary">Batal</button>
                <button type="submit" disabled={saving} className="pp-btn-primary" data-testid="du-catatan-save">{saving ? "Menyimpan…" : "Simpan Catatan"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default function AdminDaftarUlang() {
  return <DaftarUlangPage nav={NAV_ADMIN} canWrite />;
}

export function KepsekDaftarUlang() {
  return <DaftarUlangPage nav={NAV_KEPSEK} canWrite={false} />;
}
